import { useState } from "react";
import { Undo2 } from "lucide-react";
import { Banner, Button, Field, Modal, TextArea, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useReverseStoreSubscriptionPaymentMutation } from "@/modules/stores/services/mutations/useReverseStoreSubscriptionPaymentMutation";

type Props = {
  storeId: string;
  paymentId: string;
  amountLabel: string;
};

/**
 * "Reverse payment": the admin's undo for a payment recorded by mistake.
 *
 * Not a delete. The row stays, marked reversed with the reason, and the
 * wallet drops by the amount — possibly below zero, which the next top-up
 * fills first. Whatever the payment already paid for (held orders, a billing
 * period) stays paid: the seller already received that value. The reason is
 * mandatory because a reversal is an accounting entry the seller will see.
 */
export function SubscriptionPaymentReverseButton({ storeId, paymentId, amountLabel }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: reversePayment } = useReverseStoreSubscriptionPaymentMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmed = reason.trim();
  const isReasonValid = trimmed.length >= 2;

  const handleSubmit = async () => {
    if (!isReasonValid) {
      setReasonError(t(keys.admin.subscriptionPaymentReverseReasonRequired));
      return;
    }
    setReasonError(null);
    setError(null);
    setIsSubmitting(true);
    try {
      await reversePayment({ storeId, paymentId, reason: trimmed });
    } catch (caughtError) {
      setError(
        caughtError instanceof ApiJsonClientError && caughtError.message
          ? caughtError.message
          : t(keys.errors.generic),
      );
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    setIsOpen(false);
    setReason("");
    toast.show({ message: t("admin.subscriptionPaymentReversedToast", { amount: amountLabel }) });
  };

  const reasonId = `reverse-reason-${paymentId}`;
  return (
    <>
      <Button variant="ghost" size="sm" icon={Undo2} onClick={() => setIsOpen(true)}>
        {t(keys.admin.subscriptionPaymentReverseAction)}
      </Button>
      <Modal
        open={isOpen}
        onClose={() => {
          if (!isSubmitting) setIsOpen(false);
        }}
        title={t(keys.admin.subscriptionPaymentReverseConfirmTitle)}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setIsOpen(false)} disabled={isSubmitting}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmit}
              loading={isSubmitting}
              loadingLabel={t("admin.busyReversing")}
            >
              {t(keys.admin.subscriptionPaymentReverseAction)}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error ? (
            <Banner tone="error" title={t("admin.subscriptionPaymentReverseFailedTitle")}>
              {error} {t("admin.nothingChanged")}
            </Banner>
          ) : null}
          <p className="rd-modal-text" style={{ margin: 0 }}>
            {t(keys.admin.subscriptionPaymentReverseConfirmBody, { amount: amountLabel })}
          </p>
          <Field
            label={t(keys.admin.subscriptionPaymentReverseReasonLabel)}
            htmlFor={reasonId}
            error={reasonError ?? undefined}
          >
            <TextArea
              id={reasonId}
              name="reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError(null);
              }}
              onBlur={() => {
                if (reason.trim() && !isReasonValid) setReasonError(t(keys.admin.subscriptionPaymentReverseReasonRequired));
              }}
              rows={3}
              required
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
